import { Select } from "./ui/select.tsx";
import { notify } from "./feedback.ts";
import { PencilSimpleIcon, PlayIcon, XIcon } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type * as C from "@zooi/contracts";
import { useEffect, useState } from "react";
import { api, q } from "./api.ts";
import { day, hour, weekdayName } from "./format.ts";
import { RUN_SETTINGS_LABELS as L } from "./labels.ts";
import { Button, ErrorText, Field, Section } from "./ui.tsx";
import { Skeleton } from "./ui/skeleton.tsx";

// Agent › When your agent runs (design/architecture.md §2.5; moved from
// Settings › Runs in plan M6 step 6b): when zooi asks the product's agent to
// run, quiet hours for run notices (none by default, decided 2026-10-13), and
// a pause. A person's session only.

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];

export function RunSettings({ product }: { product: C.Product }) {
  return <ProductRuns product={product} />;
}

function ProductRuns({ product }: { product: C.Product }) {
  const client = useQueryClient();
  const current = useQuery(q.runSettings(product.id));
  const [form, setForm] = useState<C.UpdateRunSettingsInput | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>();
  const [editing, setEditing] = useState(false);
  useEffect(() => { if (current.data && !form) setForm({ weekday: current.data.weekday, hour: current.data.hour, quiet: current.data.quiet, paused_until: current.data.paused_until }); }, [current.data, form]);
  if (current.isLoading || !form) return <Section id="run-settings" title={L.title}>{current.error ? <ErrorText error={current.error} /> : <Skeleton shape="list" rows={1} />}</Section>;
  const saved = current.data!;
  const id = (name: string) => `run-${product.id}-${name}`;
  const set = (patch: Partial<C.UpdateRunSettingsInput>) => { setForm({ ...form, ...patch }); };
  const save = async (next = form) => {
    setBusy(true); setError(undefined);
    try {
      const updated = await api.put<C.RunSettings>(`/api/products/${encodeURIComponent(product.id)}/run-settings`, next);
      client.setQueryData(q.runSettings(product.id).queryKey, updated);
      setForm({ weekday: updated.weekday, hour: updated.hour, quiet: updated.quiet, paused_until: updated.paused_until });
      setEditing(false);
      notify.success(L.saved);
    } catch (cause) { setError(cause); }
    finally { setBusy(false); }
  };
  return <Section id="run-settings" title={L.title} actions={<Button size="sm" tone="ghost" icon={editing ? <XIcon /> : <PencilSimpleIcon />} disabled={busy} aria-expanded={editing} onClick={() => {
    setForm({ weekday: saved.weekday, hour: saved.hour, quiet: saved.quiet, paused_until: saved.paused_until });
    setError(undefined); setEditing(!editing);
  }}>{editing ? L.cancel : L.edit}</Button>}>
    {!editing ? <div className="z-flow">
      <p>{saved.paused_until ? L.paused(day(saved.paused_until)) : L.weeklySummary(weekdayName(saved.weekday), hour(saved.hour), saved.time_zone)}</p>
      {saved.quiet && <p className="z-small z-muted">{L.quietSummary(hour(saved.quiet.start), hour(saved.quiet.end))}</p>}
      <p className="z-small z-muted">{L.defaultNote}</p>
    </div> : <form className="z-flow" onSubmit={(event) => { event.preventDefault(); void save(); }}>
    <p className="z-small z-muted">{L.timeZone(current.data!.time_zone)}</p>
    <div className="z-grid2">
      <Field label={L.weekday}>
        <Select variant="form" id={id("weekday")} value={String(form.weekday)} disabled={busy} onValueChange={(nextValue) => set({ weekday: Number(nextValue) })} options={WEEKDAYS.map((value) => ({ value: String(value), label: weekdayName(value) }))} />
      </Field>
      <Field label={L.hour}>
        <Select variant="form" id={id("hour")} value={String(form.hour)} disabled={busy} onValueChange={(nextValue) => set({ hour: Number(nextValue) })} options={HOURS.map((value) => ({ value: String(value), label: hour(value) }))} />
      </Field>
    </div>
    <div className="z-flow">
      <label className="z-inline"><input type="checkbox" checked={form.quiet !== null} disabled={busy} onChange={(event) => set({ quiet: event.target.checked ? { start: 22, end: 8 } : null })} /> {L.quiet}</label>
      {form.quiet && <div className="z-grid2">
        <Field label={L.quietFrom}>
          <Select variant="form" id={id("quiet-start")} value={String(form.quiet.start)} disabled={busy} onValueChange={(nextValue) => set({ quiet: { ...form.quiet!, start: Number(nextValue) } })} options={HOURS.map((value) => ({ value: String(value), label: hour(value) }))} />
        </Field>
        <Field label={L.quietTo}>
          <Select variant="form" id={id("quiet-end")} value={String(form.quiet.end)} disabled={busy} onValueChange={(nextValue) => set({ quiet: { ...form.quiet!, end: Number(nextValue) } })} options={HOURS.map((value) => ({ value: String(value), label: hour(value) }))} />
        </Field>
      </div>}
    </div>
    <Field label={L.pause} help={L.pauseNote}>
      <input id={id("pause")} type="date" className="z-input" disabled={busy} value={form.paused_until ?? ""}
        onChange={(event) => set({ paused_until: event.target.value || null })} />
    </Field>
    {current.data?.paused_until && <p className="z-small">{L.paused(day(current.data.paused_until))} <Button size="sm" tone="ghost" icon={<PlayIcon />} disabled={busy} onClick={() => void save({ ...form, paused_until: null })}>{L.resume}</Button></p>}
    <ErrorText error={error} />
    <div className="z-inline"><Button type="submit" tone="primary" busy={busy}>{L.save}</Button></div>
    <p className="z-small z-muted">{L.note}</p>
  </form>}
  </Section>;
}
